import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Globe, Loader2, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { triggerHaptic } from '@/lib/haptics';

const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const [isChanging, setIsChanging] = useState(false);
  const [shouldBounce, setShouldBounce] = useState(false);

  const changeLanguage = async (lng: string) => {
    // Validate language before setting
    if (!['en', 'sv'].includes(lng)) {
      console.error('Invalid language selection');
      return;
    }
    
    // Don't change if already selected
    if (i18n.language === lng) {
      return;
    }
    
    try {
      setIsChanging(true);
      
      // Add a small delay for smooth transition
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Change language - this will trigger the languageChanged event in i18n config
      await i18n.changeLanguage(lng);
      
      // Show success toast
      const langName = lng === 'en' ? 'English' : 'Swedish';
      toast.success(`Language changed to ${langName}`, {
        duration: 2000,
      });
      
      // Small delay before removing loading state for smooth UX
      await new Promise(resolve => setTimeout(resolve, 200));
      setIsChanging(false);
      
      // Trigger success haptic feedback on mobile devices
      triggerHaptic('success');
      
      // Trigger bounce animation
      setShouldBounce(true);
      setTimeout(() => setShouldBounce(false), 600); // Remove bounce class after animation
    } catch (error) {
      console.error('Error changing language:', error);
      
      // Trigger error haptic feedback
      triggerHaptic('error');
      toast.error('Failed to change language');
      setIsChanging(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={isChanging}
          className={`gap-2 bg-background border-border hover:bg-[#2B2D42] hover:text-white hover:border-[#2B2D42] transition-all duration-200 shadow-sm hover:shadow-md group ${
            shouldBounce ? 'animate-[bounce_0.6s_ease-in-out]' : ''
          }`}
        >
          {isChanging ? (
            <Loader2 className="w-4 h-4 animate-spin text-primary group-hover:text-white" />
          ) : (
            <Globe className="w-4 h-4 text-primary group-hover:text-white transition-transform duration-500 group-hover:rotate-[360deg]" />
          )}
          <span className="uppercase font-semibold text-sm min-w-[24px]">
            {i18n.language}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="min-w-[160px] bg-background border-border shadow-lg z-50 animate-scale-in p-1"
      >
        <DropdownMenuItem
          onClick={() => changeLanguage('en')}
          disabled={isChanging}
          className={`cursor-pointer transition-all duration-200 rounded-md flex items-center justify-between px-3 py-2 ${
            i18n.language === 'en' 
              ? 'bg-primary text-primary-foreground font-semibold shadow-sm' 
              : 'hover:bg-[#2B2D42] hover:text-white'
          }`}
        >
          <span className="flex items-center gap-2">
            <span className="text-lg">🇬🇧</span>
            <span>English</span>
          </span>
          {i18n.language === 'en' && (
            <Check className="w-4 h-4 ml-2" />
          )}
        </DropdownMenuItem>
        
        <DropdownMenuSeparator className="my-1" />
        
        <DropdownMenuItem
          onClick={() => changeLanguage('sv')}
          disabled={isChanging}
          className={`cursor-pointer transition-all duration-200 rounded-md flex items-center justify-between px-3 py-2 ${
            i18n.language === 'sv' 
              ? 'bg-primary text-primary-foreground font-semibold shadow-sm' 
              : 'hover:bg-[#2B2D42] hover:text-white'
          }`}
        >
          <span className="flex items-center gap-2">
            <span className="text-lg">🇸🇪</span>
            <span>Swedish</span>
          </span>
          {i18n.language === 'sv' && (
            <Check className="w-4 h-4 ml-2" />
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;
